import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { ProductImage } from "../../components/ProductImage";
import { daysUntil } from "../../components/ExpirationBadge";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Flame, AlertTriangle, Clock, CheckCircle2, X, Minus, Plus, Trash2, RefreshCw } from "lucide-react";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import StatusChip from "../../components/system/StatusChip";
import EmptyState from "../../components/system/EmptyState";
import OfflineUnavailable, { CachedDataNotice } from "../../components/system/OfflineUnavailable";
import {
  saveInventory,
  readInventory,
  enqueueOutbox,
  isOnline,
} from "../../lib/offlineStore";
import syncRunner from "../../lib/syncRunner";

/**
 * Employee Action Items
 *
 * Role-focused successor to the old high-volume Removal Log. The employee
 * comes here to *do work*, not to read history:
 *
 *   - Shows only inventory that needs attention (no Fresh items)
 *   - Groups by urgency: Expired → Urgent (≤1d) → This week (≤7d)
 *   - Tap a row → quantity + reason sheet → confirm
 *   - On success: row updates (or drops off if quantity hits zero)
 *
 * Permissions, scoping, and audit history are unchanged — this page calls
 * the same `/inventory` and `/inventory/:id/remove` endpoints the legacy
 * Stock page already used. Managers/Admins still get the full historical
 * Removal Log at `/admin/removals`.
 */

const SECTIONS = [
  {
    id: "expired",
    label: "Expired",
    hint: "Past date — remove immediately",
    icon: Flame,
    tone: "expired",
    chip: "expired",
    defaultReason: "expired",
  },
  {
    id: "urgent",
    label: "Today / Within 3 days",
    hint: "Sell, mark down, or remove today",
    icon: AlertTriangle,
    tone: "urgent",
    chip: "urgent",
    defaultReason: "near_expiration",
  },
  {
    id: "attention",
    label: "Within 7 days",
    hint: "Watch this week",
    icon: Clock,
    tone: "soon",
    chip: "soon",
    defaultReason: "near_expiration",
  },
];

/**
 * UI reasons. The "Used" affordance maps to the backend `sold_out`
 * reason — same audit semantics, friendlier wording. No backend model
 * change required.
 */
const REASON_BUTTONS = [
  { key: "expired",         label: "Expired" },
  { key: "near_expiration", label: "Near expiration" },
  { key: "damaged",         label: "Damaged" },
  { key: "sold_out",        label: "Used" },
  { key: "other",           label: "Other" },
];

function relDays(iso) {
  const d = daysUntil(iso);
  if (d === null || Number.isNaN(d)) return iso || "—";
  if (d < 0)   return `${Math.abs(d)}d ago`;
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  return `in ${d}d`;
}

function shortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}

/** Map raw urgency from /inventory into the 3 section ids this page uses. */
function sectionFor(urgency) {
  if (urgency === "expired") return "expired";
  if (urgency === "urgent")  return "urgent";
  if (urgency === "attention" || urgency === "soon") return "attention";
  return null; // fresh — not shown
}

/* --------------------------- Remove Sheet -------------------------------- */

function RemoveSheet({ batch, defaultReason, onClose, onConfirm, submitting }) {
  const [qty, setQty] = useState(() => Math.max(1, batch.quantity));
  const [reason, setReason] = useState(defaultReason || "expired");
  const [note, setNote] = useState("");

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(batch.quantity, q + 1));
  const all = () => setQty(batch.quantity);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/60"
      role="dialog"
      aria-modal="true"
      data-testid="action-remove-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card
        variant="raised"
        className="w-full sm:max-w-md rounded-b-none sm:rounded-card p-5 animate-slide-up"
      >
        <div className="flex items-start gap-3 mb-4">
          <ProductImage
            src={batch.product_image_url}
            alt={batch.product_name || ""}
            size="w-14 h-14"
            className="rounded-button overflow-hidden shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="data-sku">Remove from shelf</div>
            <div
              className="font-display font-black text-lg leading-tight truncate"
              data-testid="action-remove-product-name"
            >
              {batch.product_name || "—"}
            </div>
            <div className="font-mono text-[11px] text-ink-3 mt-0.5">
              {batch.quantity} on shelf · expires {shortDate(batch.expiration_date)} ({relDays(batch.expiration_date)})
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mt-1 -mr-1 rounded-button text-ink-2 hover:text-ink"
            aria-label="Close"
            data-testid="action-remove-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Quantity stepper */}
        <div className="mb-4">
          <div className="data-sku mb-2">Quantity to remove</div>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={dec}
              disabled={qty <= 1 || submitting}
              className="h-12 w-12 rounded-button bg-paper-2 shadow-rest active:scale-[0.96] disabled:opacity-30 transition-all duration-150 flex items-center justify-center"
              data-testid="action-qty-dec"
              aria-label="Decrease quantity"
            >
              <Minus size={18} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={all}
              className="h-12 min-w-[88px] px-4 rounded-button bg-paper-sunk shadow-press font-display font-black text-2xl tabular-nums transition-all duration-150 active:scale-[0.97]"
              data-testid="action-qty-display"
              title="Tap to set to full quantity"
              aria-label={`Quantity ${qty}, tap to remove all ${batch.quantity}`}
            >
              {qty}
            </button>
            <button
              type="button"
              onClick={inc}
              disabled={qty >= batch.quantity || submitting}
              className="h-12 w-12 rounded-button bg-paper-2 shadow-rest active:scale-[0.96] disabled:opacity-30 transition-all duration-150 flex items-center justify-center"
              data-testid="action-qty-inc"
              aria-label="Increase quantity"
            >
              <Plus size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Reason chips */}
        <div className="mb-4">
          <div className="data-sku mb-2">Reason</div>
          <div className="grid grid-cols-2 gap-2">
            {REASON_BUTTONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setReason(r.key)}
                className={[
                  "h-11 px-3 rounded-button font-display text-xs uppercase tracking-wide transition-all duration-150 active:scale-[0.98]",
                  reason === r.key
                    ? "bg-ink text-white shadow-rest"
                    : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
                ].join(" ")}
                data-testid={`action-reason-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Optional note */}
        <div className="mb-5">
          <div className="data-sku mb-2">Note <span className="text-ink-3">(optional)</span></div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full p-3 rounded-button bg-paper-2 border border-border/60 shadow-rest focus:shadow-raised focus:outline-none text-sm transition-shadow"
            data-testid="action-remove-note"
            placeholder="What was the situation?"
          />
        </div>

        <Button
          tier="primary"
          className="w-full"
          onClick={() => onConfirm({ quantity: Number(qty), reason, note: note.trim() || null })}
          disabled={submitting}
          data-testid="action-confirm-remove"
        >
          {submitting ? "Removing…" : `Confirm removal · ${qty}`}
        </Button>
      </Card>
    </div>
  );
}

/* --------------------------- Page --------------------------------------- */

export default function RemovalsPage() {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [target, setTarget] = useState(null);   // batch currently being removed
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  // Offline UX state: whether the data on screen is a local cache snapshot,
  // and whether the device has *no* snapshot at all (→ show unavailable).
  const [fromCache, setFromCache] = useState(false);
  const [cacheFetchedAt, setCacheFetchedAt] = useState(null);
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  /* Initial load — offline-first. Never paint fake empty state when offline
     and no cache exists; surface <OfflineUnavailable /> instead. */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    let hadCache = false;
    if (userId) {
      try {
        const cached = await readInventory(userId);
        if (cached && Array.isArray(cached.items) && cached.items.length > 0) {
          setBatches(cached.items);
          setCacheFetchedAt(cached.fetchedAt || null);
          hadCache = true;
        }
      } catch (_) { /* cache miss is fine */ }
    }
    try {
      const { data } = await api.get("/inventory");
      const list = Array.isArray(data) ? data : [];
      setBatches(list);
      setFromCache(false);
      setUnavailableOffline(false);
      if (userId) {
        try { await saveInventory(userId, list); } catch (_) { /* never block */ }
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // Offline. If we painted from cache → mark it as cached. If we
        // didn't → show the offline-unavailable surface, NOT a fake empty.
        if (hadCache) {
          setFromCache(true);
          setUnavailableOffline(false);
        } else {
          setBatches([]);
          setFromCache(false);
          setUnavailableOffline(true);
        }
        // Suppress the red "Network Error" toast — the surface tells them.
      } else if (!silent) {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load({ silent: true });
  };

  /* Only show action items — drop fresh + anything without a section. */
  const sectioned = useMemo(() => {
    const out = { expired: [], urgent: [], attention: [] };
    for (const b of batches) {
      if (b.quantity <= 0) continue;
      const sec = sectionFor(b.urgency);
      if (sec) out[sec].push(b);
    }
    // Stable ordering inside each section: closest-to-expiry first.
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => new Date(a.expiration_date) - new Date(b.expiration_date));
    }
    return out;
  }, [batches]);

  const totals = useMemo(() => ({
    expired:   sectioned.expired.length,
    urgent:    sectioned.urgent.length,
    attention: sectioned.attention.length,
    all:       sectioned.expired.length + sectioned.urgent.length + sectioned.attention.length,
  }), [sectioned]);

  const toggleSection = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* Remove confirmation — calls the existing endpoint; permissions and
     audit log are unchanged. */
  const confirmRemove = async ({ quantity, reason, note }) => {
    if (!target) return;
    setSubmitting(true);
    const body = { quantity, reason, note };

    const queueOffline = async () => {
      if (!userId) return false;
      try {
        await enqueueOutbox(userId, {
          kind: "remove_inventory",
          url: `/inventory/${target.id}/remove`,
          body,
          targetId: target.id,
        });
        return true;
      } catch (_) { return false; }
    };

    const applyOptimistic = () => {
      setBatches((prev) => {
        const next = prev
          .map((b) => b.id === target.id ? { ...b, quantity: Math.max(0, b.quantity - quantity) } : b)
          .filter((b) => b.quantity > 0);
        if (userId) saveInventory(userId, next).catch(() => {});
        return next;
      });
    };

    /* Offline-first: enqueue to outbox when offline; flushed by syncRunner. */
    if (!isOnline()) {
      const ok = await queueOffline();
      if (ok) {
        toast.success(`Queued offline: remove ${quantity} × ${target.product_name || "item"}`);
        applyOptimistic();
        setTarget(null);
        try { syncRunner.kick("removals-queue"); } catch (_) { /* noop */ }
      } else {
        toast.error("Could not queue — please try again");
      }
      setSubmitting(false);
      return;
    }

    try {
      await api.post(`/inventory/${target.id}/remove`, body);
      const remaining = Math.max(0, (Number(target.quantity) || 0) - quantity);
      toast.success(
        remaining === 0
          ? `Cleared: ${target.product_name || "item"}`
          : `Removed ${quantity} × ${target.product_name || "item"} (${remaining} left)`
      );
      setTarget(null);
      applyOptimistic();
      await load({ silent: true });
    } catch (e) {
      if (!e?.response) {
        // Network blip mid-flight → queue it.
        const ok = await queueOffline();
        if (ok) {
          toast.success(`Queued: remove ${quantity} × ${target.product_name || "item"}`);
          applyOptimistic();
          setTarget(null);
          try { syncRunner.kick("removals-queue"); } catch (_) {}
        } else {
          toast.error("Could not queue — please try again");
        }
      } else {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pt-6 pb-28" data-testid="action-items-page">
      {/* Header */}
      <div className="mb-5 flex items-end justify-between gap-3">
        <div>
          <div className="data-sku mb-1">Today's work</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl leading-none tracking-tighter text-ink">
            Action items
          </h1>
        </div>
        <Button
          tier="secondary"
          onClick={refresh}
          disabled={refreshing}
          data-testid="action-refresh"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="action-summary-tiles">
        {SECTIONS.map((s) => {
          const count = totals[s.id];
          return (
            <Card variant="raised" className="p-3" key={s.id} data-testid={`action-tile-${s.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="data-sku truncate">{s.label}</div>
                <div className={`h-6 w-6 rounded-chip flex items-center justify-center bg-status-${s.tone}-bg text-status-${s.tone}`}>
                  <s.icon size={12} strokeWidth={2.5} />
                </div>
              </div>
              <div className={`font-display font-black text-2xl tabular-nums leading-none ${count > 0 ? "text-ink" : "text-ink-3"}`}>
                {count}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Body */}
      {loading && batches.length === 0 && !unavailableOffline && (
        <Card variant="raised" className="p-6">
          <div className="data-sku text-center">Loading inventory…</div>
        </Card>
      )}

      {!loading && unavailableOffline && (
        <OfflineUnavailable
          title="Inventory unavailable offline"
          description="This device does not currently have a local inventory snapshot. Reconnect to refresh inventory."
          onRetry={refresh}
          data-testid="action-items-offline-unavailable"
        />
      )}

      {!loading && !unavailableOffline && fromCache && (
        <CachedDataNotice fetchedAt={cacheFetchedAt} data-testid="action-items-cached-notice" />
      )}

      {!loading && !unavailableOffline && totals.all === 0 && (
        <EmptyState
          icon={<CheckCircle2 size={36} strokeWidth={1.5} className="text-status-fresh" />}
          title="Nothing to remove right now"
          description="Every item on your shelf has more than 7 days of life. Check back later or refresh to re-scan."
          action={
            <Button tier="secondary" onClick={refresh} data-testid="action-empty-refresh">
              <RefreshCw size={14} /> Refresh
            </Button>
          }
          data-testid="action-items-empty"
        />
      )}

      {!loading && !unavailableOffline && totals.all > 0 && (
        <div data-testid="action-sections">
          {SECTIONS.map((s) => {
            const rows = sectioned[s.id];
            if (rows.length === 0) return null;
            const isCollapsed = collapsed.has(s.id);
            return (
              <section
                key={s.id}
                className="mb-5"
                data-testid={`action-section-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(s.id)}
                  className="w-full flex items-center gap-2 mb-2 text-left"
                  aria-expanded={!isCollapsed}
                  data-testid={`action-section-toggle-${s.id}`}
                >
                  <s.icon size={14} className={`text-status-${s.tone} shrink-0`} strokeWidth={2.5} />
                  <span className="font-display font-bold text-[12px] uppercase tracking-wider text-ink">
                    {s.label}
                  </span>
                  <span className="font-mono text-[11px] text-ink-3 truncate">
                    {rows.length} {rows.length === 1 ? "item" : "items"} · {s.hint}
                  </span>
                </button>

                {!isCollapsed && (
                  <Card variant="raised" className="overflow-hidden">
                    {rows.map((b, idx) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setTarget(b)}
                        className={`w-full flex items-center gap-3 p-3 text-left active:bg-paper-sunk transition-colors ${idx > 0 ? "border-t border-border/40" : ""}`}
                        data-testid={`action-row-${b.id}`}
                      >
                        <ProductImage
                          src={b.product_image_url}
                          alt={b.product_name || ""}
                          size="w-12 h-12"
                          className="rounded-button overflow-hidden shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div
                            className="font-display font-semibold text-sm leading-tight truncate"
                            data-testid={`action-row-name-${b.id}`}
                          >
                            {b.product_name || "—"}
                          </div>
                          <div className="mt-0.5 font-mono text-[11px] text-ink-3 truncate">
                            <span className="text-ink font-semibold tabular-nums">{b.quantity}</span>
                            <span> on shelf</span>
                            <span className="opacity-60"> · </span>
                            <span>{shortDate(b.expiration_date)}</span>
                            <span className="opacity-60"> · </span>
                            <span className={`text-status-${s.tone}`}>{relDays(b.expiration_date)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <StatusChip tone={s.chip}>{SECTIONS.find((x) => x.id === s.id).label.split(" /")[0]}</StatusChip>
                          <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-chip bg-paper-sunk shadow-rest text-[11px] font-mono uppercase tracking-wider text-ink-2">
                            <Trash2 size={11} /> Remove
                          </span>
                        </div>
                      </button>
                    ))}
                  </Card>
                )}
              </section>
            );
          })}
        </div>
      )}

      {target && (
        <RemoveSheet
          batch={target}
          defaultReason={SECTIONS.find((s) => s.id === sectionFor(target.urgency))?.defaultReason || "expired"}
          onClose={() => !submitting && setTarget(null)}
          onConfirm={confirmRemove}
          submitting={submitting}
        />
      )}
    </div>
  );
}
